// node_modules
import { useEffect, useRef, useState } from "react";
import { faBookmark as faBookmarkSolid, faTimes } from "@fortawesome/free-solid-svg-icons";
import { faBookmark as faBookmarkRegular } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// styles
import styles from "./findestBookmark.module.scss";
// Types
import { TAddPartnerToTechnologyParameter, TBookmarkOnDTO, TMagDocumentDTO, TPartnerResultDTO, TUsPatentDTO, TWebDocumentDTO } from "Types";
// Custom hooks
import { useClickOutsideRef } from "Hooks";
// Enums
import { EntityTypeEnum } from "Enums";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";
// Logics
import { BookmarkLogicSingleton } from "Logics";
import { PendoHelperSingleton } from "Helpers";
import { LoggingConstants } from "Constants";

type TFindestBookmarkProps = {
    entity: TMagDocumentDTO | TUsPatentDTO | TPartnerResultDTO | TWebDocumentDTO,
    entityType: EntityTypeEnum,
    isEntityBookmarked: boolean,
    isEditable?: boolean
    coworkerBookmarkOn?: TBookmarkOnDTO[],
    scoutBookmarkOn?: TBookmarkOnDTO[],
    handleEntityHasBeenUnbookmarked?: (unbookmarkOnEntityId: string) => Promise<void>,
    handleEntityHasBeenBookmarked?: (bookmarkOnEntityId: string) => Promise<void>,
}

export const FindestBookmark = ({ entity, entityType, isEntityBookmarked, isEditable, coworkerBookmarkOn, scoutBookmarkOn, handleEntityHasBeenUnbookmarked, handleEntityHasBeenBookmarked }: TFindestBookmarkProps) => {
    // Ref
    const bookmarkOnContainerRef = useRef<HTMLDivElement>(null);
    // State
    const [isBookmarked, setIsBookmarked] = useState<boolean>(isEntityBookmarked);
    const [bookmarkOnList, setBookmarkOnList] = useState<TBookmarkOnDTO[] | undefined>(coworkerBookmarkOn);
    const [isBookmarkOnShown, setIsBookmarkOnShown] = useState<boolean>(false);

    // Logic
    const handleClickOutsideRef = (): void => {
        setIsBookmarkOnShown(false);
    };

    const bookmark = async(bookmarkOnEntityId: string, bookmarkOnEntityType: EntityTypeEnum): Promise<void> => {
        if (!bookmarkOnList) {return;}
        
        if (bookmarkOnEntityType === EntityTypeEnum.TechnologyType) {
            const technologyId: number = parseInt(bookmarkOnEntityId, 10);
            switch (entityType) {
                case EntityTypeEnum.ScienceArticleType:
                    entity = entity as TMagDocumentDTO;
                    await TechnologyControllerSingleton
                        .addScienceArticleToTechnologyAsync(technologyId, entity.id, true);
                    PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}BookmarkScienceArticle`, {
                        technologyId: technologyId, 
                        articleId: entity.id
                    });
                    break;
                case EntityTypeEnum.PatentType:
                    entity = entity as TUsPatentDTO;
                    await TechnologyControllerSingleton
                        .addPatentToTechnologyAsync(technologyId, entity.id, true);
                    PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}BookmarkPatent`, {
                        technologyId: technologyId, 
                        patentId: entity.id
                    });
                    break;
                case EntityTypeEnum.OrganizationType:
                    entity = entity as TPartnerResultDTO;
                    await TechnologyControllerSingleton
                        .addPartnerFromDeliveryToTechnologyAsync(technologyId, {
                            name: entity.name,
                            partnerType: entity.partnerType,
                            patents: entity.patents,
                            scienceArticles: entity.scienceArticles,
                            imageUrl: entity.imageUrl
                        } as TAddPartnerToTechnologyParameter);
                    PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}BookmarkPartner`, {
                        technologyId: technologyId, 
                        name: entity.name,
                        partnerType: entity.partnerType,
                    });
                    break;
                default:
                    return;
            }
        }

        for (const bookmarkOnEntity of bookmarkOnList) {
            if (bookmarkOnEntity.id === bookmarkOnEntityId) {
                bookmarkOnEntity.isBookmarkedOn = true;
            }
        }

        setIsBookmarked(BookmarkLogicSingleton.isResultBookmarked(coworkerBookmarkOn, scoutBookmarkOn));
        orderBookmarkOnListByIsBookmarkedOn();

        if (handleEntityHasBeenBookmarked) {await handleEntityHasBeenBookmarked(bookmarkOnEntityId);}
    };

    const unbookmark = async(unbookmarkOnEntityId: string, unbookmarkOnEntityType: EntityTypeEnum): Promise<void> => {
        if (!bookmarkOnList) {return;}
    
        if (unbookmarkOnEntityType === EntityTypeEnum.TechnologyType) {
            const technologyId: number = parseInt(unbookmarkOnEntityId, 10);
            switch (entityType) {
                case EntityTypeEnum.ScienceArticleType:
                    entity = entity as TMagDocumentDTO;
                    await TechnologyControllerSingleton
                        .removeScienceArticleFromDeliveryToTechnologyAsync(technologyId, entity.id);
                    break;
                case EntityTypeEnum.PatentType:
                    entity = entity as TUsPatentDTO;
                    await TechnologyControllerSingleton
                        .removePatentFromDeliveryToTechnologyAsync(technologyId, entity.id);
                    break;
                case EntityTypeEnum.OrganizationType:
                    entity = entity as TPartnerResultDTO;
                    await TechnologyControllerSingleton
                        .removePartnerFromTechnologyAsync(technologyId, entity.name);
                    break;
                default:
                    return;
            }
        }

        for (const bookmarkOnEntity of bookmarkOnList) {
            if (bookmarkOnEntity.id === unbookmarkOnEntityId) {
                bookmarkOnEntity.isBookmarkedOn = false;
            }
        }

        setIsBookmarked(BookmarkLogicSingleton.isResultBookmarked(coworkerBookmarkOn, scoutBookmarkOn));
        orderBookmarkOnListByIsBookmarkedOn();

        if (handleEntityHasBeenUnbookmarked) {await handleEntityHasBeenUnbookmarked(unbookmarkOnEntityId);}
    };

    useClickOutsideRef(bookmarkOnContainerRef, handleClickOutsideRef);

    const handleBookmarkIconOnClick = (): void => {
        setIsBookmarkOnShown(!isBookmarkOnShown);
    };

    useEffect(() => {
        setIsBookmarked(isEntityBookmarked);
    }, [entity, isEntityBookmarked]);

    const orderBookmarkOnListByIsBookmarkedOn = (): void => {
        if (!bookmarkOnList) {return;}

        const bookmarkedOnList: TBookmarkOnDTO[] = [];
        const notBookmarkedOnList: TBookmarkOnDTO[] = [];

        for (const bookmarkOn of bookmarkOnList) {
            if (bookmarkOn.isBookmarkedOn) {
                bookmarkedOnList.push(bookmarkOn);
            } else {
                notBookmarkedOnList.push(bookmarkOn);
            }
        }

        setBookmarkOnList([...bookmarkedOnList, ...notBookmarkedOnList]);
    };

    const getBookmarkOnClickHandler = (bookmarkOnEntity: TBookmarkOnDTO) => {
        if (!bookmarkOnEntity.isBookmarkedOn) {
            return () => bookmark(bookmarkOnEntity.id, bookmarkOnEntity.type);
        } else {
            return undefined;
        }
    };

    return (
        <span className={styles.bookmarkContainer}>
            <FontAwesomeIcon
                icon={isBookmarked ? faBookmarkSolid : faBookmarkRegular}
                className={isBookmarked ? styles.entityBookmarked : styles.entityNotBookmarked}
                onClick={isEditable ? handleBookmarkIconOnClick : undefined} />
            {bookmarkOnList && isBookmarkOnShown ?
                <div className={[styles.bookmarkReferenceContainer, styles.scrollbarCustomThin].join(" ")} ref={bookmarkOnContainerRef}>
                    {bookmarkOnList.map(bookmarkOnEntity => {
                        return (
                            <div className={styles.bookmarkReference} key={`${bookmarkOnEntity.id}-${bookmarkOnEntity.displayName}`} onClick={isEditable ? getBookmarkOnClickHandler(bookmarkOnEntity) : undefined} >
                                <div className={styles.bookmarkReferenceTitle} title={bookmarkOnEntity.displayName}>
                                    {bookmarkOnEntity.displayName}
                                </div>
                                {bookmarkOnEntity.isBookmarkedOn ?
                                    <div className={styles.bookmarkRefernceRemove} title={"Remove bookmark"}>
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            onClick={isEditable ? () => unbookmark(bookmarkOnEntity.id, bookmarkOnEntity.type) : undefined} />
                                    </div>
                                    :
                                    null
                                }
                            </div>
                        );
                    })
                    }
                </div>
                :
                null
            }
        </span>
    );
};